import { useMemo, useState, type ReactNode } from "react";
import { BookingCard } from "../actions/booking-card";
import type { ActionCardHostProps } from "../actions/card-props";
import { CardNotice } from "../actions/card-shell";
import type { CardState, ConfirmRequest, ConfirmResponse, ResultCard, SlotsResponse } from "../actions/contract";
import { visitorTimeZone } from "../actions/dates";
import { useActionLabels, type ActionLabels } from "../actions/labels";
import { ResultCardView } from "../actions/result-card";
import type { FetchSlots, SlotRange } from "../actions/use-slots";
import type { BookTourPayload, ChatAction, LeadFormPayload } from "../types";
import type { WorkflowChoice, WorkflowStepPayload } from "../workflow/contract";
import type { WorkflowCardLabels } from "../workflow/labels";
import type { WorkflowAnswerExtras } from "../workflow/step";
import { HandoffOfferCard } from "./action-cards";
import { LeadFormCard } from "./lead-form-card";
import { WorkflowStepCard } from "./workflow-step-card";

export interface ActionHandlers {
  /** `GET /public/v1/actions/{run_id}/slots`. */
  loadSlots?: (runId: string, range: SlotRange) => Promise<SlotsResponse>;
  /** `POST /public/v1/actions/{run_id}/confirm`. */
  confirmAction?: (runId: string, request: ConfirmRequest) => Promise<ConfirmResponse>;
  /** A card turned into its result / expired: keep the transcript in step (`useChatStream().setActionState`). */
  onActionState?: (messageId: string, state: CardState, result?: ResultCard) => void;
  renderCaptcha?: ActionCardHostProps["renderCaptcha"];
  actionLabels?: Partial<ActionLabels>;
  actionLocale?: string;
  actionTimeZone?: string;
  /** Console playground: booking / lead cards render as a disabled preview. */
  previewActions?: boolean;
  showActionMeta?: boolean;
  onAcceptHandoff?: () => void;
  onAskElse?: () => void;
  /** Renders `custom` actions. */
  renderCustom?: (payload: Record<string, unknown>) => ReactNode;
  /** The assistant's ordering cards (`commerce_card`, SD-S2-17): the host app draws them; without it the message text stands. */
  renderCommerce?: (payload: Record<string, unknown>) => ReactNode;
  /** The visitor answered a workflow card: send it with `answerWorkflowStep(chat.send, card, choice)`. Without it cards are read-only. */
  onWorkflowAnswer?: (card: WorkflowStepPayload, choice: WorkflowChoice, extras?: WorkflowAnswerExtras) => void;
  /** The bot check of "Send code" / "Resend code" on a code card (its own widget: the token is tied to the `otp_request` action). */
  renderWorkflowCaptcha?: ActionCardHostProps["renderCaptcha"];
  workflowLabels?: Partial<WorkflowCardLabels>;
  /** A turn is in flight: workflow cards cannot be answered right now. */
  workflowBusy?: boolean;
  /** Id of the element holding the message text (workflow cards are described by it). */
  textId?: string;
}

type CardPayload = BookTourPayload | LeadFormPayload;

interface CardGateProps {
  payload: CardPayload;
  handlers: ActionHandlers;
  messageId: string | undefined;
  renderLive: (host: ActionCardHostProps, loadSlots: FetchSlots | undefined) => ReactNode;
}

/**
 * The card as it was when it appeared: pending → the live card (which turns into its result in
 * place); restored `succeeded` / `expired` / `unavailable` (or an offer without a run) → a static view.
 */
function CardGate({ payload, handlers, messageId, renderLive }: CardGateProps) {
  const labels = useActionLabels(handlers.actionLabels);
  const timeZone = useMemo(() => handlers.actionTimeZone ?? visitorTimeZone(), [handlers.actionTimeZone]);
  const [view] = useState<CardState>(() => (payload.state === "pending" && !payload.run_id && !handlers.previewActions ? "unavailable" : payload.state));
  if (view === "succeeded") {
    if (payload.result) return <ResultCardView result={payload.result} labels={labels} timeZone={timeZone} locale={handlers.actionLocale} />;
    return <CardNotice icon="check-circle" title={payload.title} text={labels.completed} />;
  }
  if (view === "expired") return <CardNotice icon="clock" title={payload.title} text={labels.expired} />;
  if (view === "unavailable") return <CardNotice icon="alert-circle" title={payload.title} text={labels.unavailable} />;

  const { run_id: runId } = payload;
  const { confirmAction, loadSlots, onActionState } = handlers;
  const host: ActionCardHostProps = {
    confirm: runId && confirmAction ? (request) => confirmAction(runId, request) : undefined,
    onStateChange:
      messageId && onActionState
        ? (state, result) => {
            onActionState(messageId, state, result);
          }
        : undefined,
    renderCaptcha: handlers.renderCaptcha,
    labels: handlers.actionLabels,
    locale: handlers.actionLocale,
    timeZone: handlers.actionTimeZone,
    preview: handlers.previewActions,
    showMeta: handlers.showActionMeta,
  };
  return renderLive(host, runId && loadSlots ? (range) => loadSlots(runId, range) : undefined);
}

function NO_ANSWER(): void {
  // Read-only host: the card renders disabled and never calls this.
}

export interface ActionCardProps {
  action: ChatAction;
  handlers: ActionHandlers;
  /** Id of the message carrying the card (for `onActionState`). */
  messageId?: string;
}

/** Action card dispatcher. `handoff` renders nothing here: the live handoff lines run in the log. */
export function ActionCard({ action, handlers, messageId }: ActionCardProps) {
  switch (action.type) {
    case "book_tour":
      return <CardGate payload={action.payload} handlers={handlers} messageId={messageId} renderLive={(host, loadSlots) => <BookingCard payload={action.payload} loadSlots={loadSlots} {...host} />} />;
    case "lead_form":
      return <CardGate payload={action.payload} handlers={handlers} messageId={messageId} renderLive={(host) => <LeadFormCard payload={action.payload} {...host} />} />;
    case "workflow_step":
      return <WorkflowStepCard payload={action.payload} onAnswer={handlers.onWorkflowAnswer ?? NO_ANSWER} disabled={handlers.workflowBusy === true || !handlers.onWorkflowAnswer} describedBy={handlers.textId} labels={handlers.workflowLabels} renderCaptcha={handlers.renderWorkflowCaptcha} />;
    case "handoff_offer":
      return <HandoffOfferCard payload={action.payload} onAccept={() => handlers.onAcceptHandoff?.()} onAskElse={() => handlers.onAskElse?.()} />;
    case "custom":
      return <>{handlers.renderCustom?.(action.payload)}</>;
    case "commerce_card":
      return <>{handlers.renderCommerce?.(action.payload)}</>;
    case "handoff":
      return null;
  }
}
